import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const { lang, t, login, signup } = useApp();
  const navigate = useNavigate();
  const isLogin = mode === "login";
  const head = lang === "hi" ? "font-hi" : "font-display";

  const [form, setForm] = useState({ email: "", password: "", store_name: "", whatsapp_number: "" });
  const [busy, setBusy] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
      } else {
        await signup(form);
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2" data-testid="auth-page">
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/10317113/pexels-photo-10317113.jpeg"
          alt="Saree pattern"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#6B1F3A]/60" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C9A84C] mb-4 font-semibold">
            ✦ Heritage Reimagined
          </div>
          <h2 className={`${head} text-4xl xl:text-5xl font-light leading-tight`}>
            Drape every customer
            <br />
            <span className="italic">in a saree, instantly.</span>
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 sm:px-8 py-12">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="auth-form">
          <Link to="/" className="text-xs tracking-[0.25em] uppercase text-[#635057] hover:text-[#6B1F3A]" data-testid="auth-back-link">
            ← {t.common.back}
          </Link>
          <h1 className={`${head} text-3xl sm:text-4xl text-[#2D1820] mt-6 font-light tracking-tight`} data-testid="auth-title">
            {isLogin ? t.auth.loginTitle : t.auth.signupTitle}
          </h1>

          <div className="mt-8 space-y-5">
            {!isLogin && (
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{t.auth.storeName}</label>
                <input name="store_name" value={form.store_name} onChange={onChange} required className="field mt-2" data-testid="auth-store-name" />
              </div>
            )}
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{t.auth.email}</label>
              <input name="email" type="email" value={form.email} onChange={onChange} required className="field mt-2" data-testid="auth-email" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{t.auth.password}</label>
              <input name="password" type="password" minLength={6} value={form.password} onChange={onChange} required className="field mt-2" data-testid="auth-password" />
            </div>
            {!isLogin && (
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#635057] font-semibold">{t.auth.whatsapp}</label>
                <input name="whatsapp_number" value={form.whatsapp_number} onChange={onChange} placeholder="+919876543210" className="field mt-2" data-testid="auth-whatsapp" />
                <div className="text-[11px] text-[#635057] mt-1">{t.auth.whatsappHelp}</div>
              </div>
            )}
          </div>

          <button disabled={busy} className="btn-primary w-full mt-8" data-testid="auth-submit-btn">
            {busy ? t.common.loading : isLogin ? t.auth.login : t.auth.signup}
          </button>

          <div className="mt-6 text-center">
            <Link
              to={isLogin ? "/signup" : "/login"}
              className="text-[12px] tracking-[0.18em] uppercase text-[#6B1F3A] hover:text-[#C9A84C]"
              data-testid="auth-switch-link"
            >
              {isLogin ? t.auth.switchToSignup : t.auth.switchToLogin}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
